import { Button, NotFoundContainer } from '@/components/ui';
import { useGetAvailableDate } from '@/services';
import { getProfessionalData } from '@/utils/cookies';
import { Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	groupAvailableDatesByWeekday,
	GroupedSchedule,
} from './utils/groupAvailableDatesByWeekday';
import { CalendarSlotsCard, CalendarSlotsCardSkeleton } from './components';

const Availability = () => {
	const navigate = useNavigate();
	const { id: professionalId } =
		getProfessionalData() as Professionals.Professional;
	const { data, isLoading } = useGetAvailableDate(professionalId);

	const [localAvailableDates, setLocalAvailableDates] = useState<
		AvailableDates.AvailableDate[] | null
	>(null);

	// Sempre que vier dado novo do back, sincroniza o estado local
	useEffect(() => {
		if (data?.availableDates) {
			setLocalAvailableDates(data.availableDates);
		}
	}, [data]);

	const handleDeleteFromFront = (deletedIds: string[]) => {
		setLocalAvailableDates((prev) =>
			prev ? prev.filter((item) => !deletedIds.includes(item.id)) : prev,
		);
	};

	const baseDates = localAvailableDates ?? data?.availableDates ?? null;

	const groupedSchedule: Array<GroupedSchedule> | undefined = useMemo(
		() => (baseDates ? groupAvailableDatesByWeekday(baseDates) : undefined),
		[baseDates],
	);

	const SKELETON_ITEMS_COUNT = 3;

	return (
		<div className="h-full space-y-6">
			<div className="flex w-full items-center justify-between gap-2">
				<h2 className="whitespace-nowrap text-lg font-medium text-dark">
					Horários disponíveis
				</h2>
				<Button
					text="Criar horário"
					className="h-fit w-fit justify-center rounded-full bg-black px-6 py-2 text-sm font-bold shadow-md hover:bg-neutral-800 md:text-base"
					startIcon={
						<Plus className="size-5 stroke-[3] text-white" />
					} // Aumentei levemente o ícone e a espessura para bater com a imagem
					onClick={() =>
						navigate('/profissional/horarios/disponibilidade/criar')
					}
				/>
			</div>

			{isLoading ? (
				<div className="flex w-fit flex-wrap gap-4">
					{[...Array(SKELETON_ITEMS_COUNT)].map((_, index) => (
						<CalendarSlotsCardSkeleton key={index} />
					))}
				</div>
			) : groupedSchedule && groupedSchedule.length > 0 ? (
				<div className="flex w-fit flex-wrap gap-4">
					{groupedSchedule.map((group, index) => (
						<CalendarSlotsCard
							key={index}
							groupedSchedule={group}
							onDelete={handleDeleteFromFront}
						/>
					))}
				</div>
			) : (
				<NotFoundContainer
					className="mx-auto mt-10 lg:!mt-32"
					title="Nenhum resultado encontrado"
					message={
						<div className="flex justify-center gap-1">
							<Button
								onClick={() =>
									navigate(
										'/profissional/horarios/disponibilidade/criar',
									)
								}
								className="rounded-none border-0 !border-b border-solid border-blue-500 px-0 py-0 !leading-3 text-blue-500"
								text="Criar"
								variant="text"
							/>
							<span>horário de disponibilidade</span>
						</div>
					}
				/>
			)}
		</div>
	);
};

export { Availability };
